import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom'

import { LOGIN_PATH } from 'account/login/Login'
import { Layout as AntLayout, Menu, Skeleton } from 'antd'
import Logo from 'ichraDecisionTool/common/Logo'

import { User } from 'core/application/account/loginUser'
import localStorage from 'core/application/commons/localStorage'
import { AppState } from 'core/application/commons/reduxStore'
import getProspectPermissions, { ProspectPermissions } from 'core/application/ichraDecisionTool/getProspectPermissions'
import stateApi, { State } from 'core/application/ichraDecisionTool/prospectSignup/state'
import wizardApi from 'core/application/ichraDecisionTool/prospectSignup/wizard'
import { StepStatus, WizardStep } from 'core/domain/wizard/wizard'

import Modal from 'common/components/feedback/Modal'
import notification from 'common/components/feedback/Notification'

import useLoginUser from 'common/hooks/useLoginUser'

import { queryRequest } from 'common/RequestUtils'
import Layout from 'common/layout/Layout'
import { ROUTE_PATHS } from 'common/routePaths'

import NotFound from 'errorPages/NotFound'

import { ICHRA_DECISION_TOOL_SIGNUP_PATH, RoutesMapping } from './Routes'
import { ActiveItemProps, DEFAULT_CATEGORY_INDEX, DEFAULT_CATEGORY_ITEM_INDEX } from './SignUpNavigationMenu'
import SignUpNavigationMenu from './SignUpNavigationMenu'
import { GetActiveItem, getNavigationItemFullPath } from './helpers'

import './SignupStyles.less'

const { ICHRA_DECISION_TOOL_PATH } = ROUTE_PATHS

const { Content } = AntLayout

interface ProspectSignupProps {}

const ProspectSinup: React.FC<ProspectSignupProps> = () => {
  const history = useHistory()
  const location = useLocation()
  const [user] = useLoginUser()
  const [navigationItems, setNavigationItems] = useState<WizardStep[]>([])
  const [prospectSignupState, setProspectSignupState] = useState<State | undefined>()
  const [activeItem, setActiveItem] = useState<ActiveItemProps>()
  const [hasDirtyData, setHasDirtyData] = useState<boolean>(false)

  useEffect(() => {
    userIsAllowedToEnterProspectSignUpFlow()
      .then((res) => fetchWIzardInfo())
      .catch((err) => redirectToLogin())
  }, [])

  const redirectToLogin = () => {
    history.push(LOGIN_PATH)
  }

  const fetchWIzardInfo = () => {
    wizardApi().then((res: any) => {
      if (res.errors && res.errors.length > 0) {
        notification.open({
          message: res.errors[0],
          type: 'error',
        })
      } else {
        setNavigationItems(res.steps)
      }
    })
  }

  useEffect(() => {
    if (navigationItems && navigationItems.length > 0) {
      setActiveItem(GetActiveItem(location.pathname, navigationItems))
      setProspectSignupState(undefined)
      stateApi().then((res: any) => {
        if (res.errors && res.errors.length > 0) {
          notification.open({
            message: res.errors[0],
            type: 'error',
          })
        } else {
          setProspectSignupState(res)
          setHasDirtyData(false)
        }
      })
    }
  }, [location, navigationItems])

  const DynamicContentBasedOnRoutes = (
    goBack: (from: string) => void,
    goNext: (from: string) => void,
    hasDirtyData: (dirtyData: boolean) => void,
  ) => {
    let match = useRouteMatch()

    return prospectSignupState ? (
      <Switch>
        {RoutesMapping.map((item) => {
          return (
            <Route
              key={item.key}
              path={`${match.path}${item.route}`}
              render={(props) => (
                <item.component
                  name={item.key}
                  goBack={goBack}
                  goNext={goNext}
                  hasDirtyData={hasDirtyData}
                  state={item.mapState(prospectSignupState)}
                  {...props}
                />
              )}
            />
          )
        })}
        <Route>
          <NotFound />
        </Route>
      </Switch>
    ) : null
  }

  const userIsAllowedToEnterProspectSignUpFlow = async (): Promise<boolean> => {
    if (localStorage.getProspectSignupCode() === '') return Promise.reject(false)
    const res: ProspectPermissions[] = await queryRequest(() => getProspectPermissions())
    return res.includes(ProspectPermissions.AccessWizard) ? Promise.resolve(true) : Promise.reject(false)
  }

  const GetPreviousItem = (currentItem: string): WizardStep => {
    try {
      const currentCategoryIndex = navigationItems.findIndex((cat) =>
        cat.children.some((item) => item.id === currentItem),
      )
      if (currentCategoryIndex === -1) throw new Error('Category not found')
      const currentCategory = navigationItems[currentCategoryIndex]
      const itemIndex = currentCategory.children.findIndex((item) => item.id === currentItem)
      if (itemIndex === -1) throw new Error('Category item not found')
      if (itemIndex > 0) {
        return currentCategory.children[itemIndex - 1]
      } else if (currentCategoryIndex > 0) {
        const previousCategory = navigationItems[currentCategoryIndex - 1]
        if (previousCategory === undefined) throw new Error('Previous Category not found')
        return previousCategory.children[previousCategory.children.length - 1]
      }
      return navigationItems[DEFAULT_CATEGORY_INDEX].children[DEFAULT_CATEGORY_ITEM_INDEX]
    } catch (e) {
      console.log(e)
      return navigationItems[DEFAULT_CATEGORY_INDEX].children[DEFAULT_CATEGORY_ITEM_INDEX]
    }
  }

  const GetNextItem = (currentItem: string): WizardStep => {
    try {
      const currentCategoryIndex = navigationItems.findIndex((cat) =>
        cat.children.some((item) => item.id === currentItem),
      )
      if (currentCategoryIndex === -1) throw new Error('Category not found')
      const currentCategory = navigationItems[currentCategoryIndex]
      const itemIndex = currentCategory.children.findIndex((item) => item.id === currentItem)
      if (itemIndex === -1) throw new Error('Category item not found')
      const item = currentCategory.children[itemIndex]
      item.status = StepStatus.Complete
      if (itemIndex < currentCategory.children.length - 1) {
        let nextItem = currentCategory.children[itemIndex + 1]
        if (nextItem.status === StepStatus.Disabled) {
          nextItem.status = StepStatus.Active
        }
        return nextItem
      } else if (currentCategoryIndex < navigationItems.length - 1) {
        const nextCategory = navigationItems[currentCategoryIndex + 1]
        if (nextCategory === undefined) throw new Error('Next Category not found')
        let nextItem = nextCategory.children[0]
        if (nextItem.status === StepStatus.Disabled) {
          nextItem.status = StepStatus.Active
        }
        return nextItem
      }
      return navigationItems[DEFAULT_CATEGORY_INDEX].children[DEFAULT_CATEGORY_ITEM_INDEX]
    } catch (e) {
      console.log(e)
      return navigationItems[DEFAULT_CATEGORY_INDEX].children[DEFAULT_CATEGORY_ITEM_INDEX]
    }
  }

  const goBack = (from: string) => {
    const prevItem = GetPreviousItem(from)
    const prevItemPath = getNavigationItemFullPath(prevItem)
    if (hasDirtyData) {
      Modal.confirm({
        title: 'Are you sure you want to leave the page?',
        content: 'You may lose changes you have made to this page.',
        onOk() {},
        onCancel() {
          history.push(prevItemPath)
        },
        okText: 'Stay',
        okType: 'primary',
        cancelText: 'Discard',
      })
    } else {
      history.push(`${ICHRA_DECISION_TOOL_PATH}/${prevItemPath}`)
    }
  }

  const goNext = (from: string) => {
    const nextItem = GetNextItem(from)
    const nextItemPath = getNavigationItemFullPath(nextItem)

    history.push(`${ICHRA_DECISION_TOOL_PATH}/${nextItemPath}`)
  }

  const redirectTo = (path: string) => {
    if (hasDirtyData) {
      Modal.confirm({
        title: 'Are you sure you want to leave the page?',
        content: 'You may lose changes you have made to this page.',
        onOk() {},
        onCancel() {
          history.push(path)
        },
        okText: 'Stay',
        okType: 'primary',
        cancelText: 'Discard',
      })
    } else {
      history.push(path)
    }
  }

  return (
    <Skeleton
      active
      loading={navigationItems.length === 0}
      paragraph={{
        rows: 10,
      }}
      className="skeleton"
    >
      <AntLayout>
        <Menu mode="horizontal" className="pl-48">
          <Logo />
        </Menu>
        <AntLayout className="signup_wrapper">
          <SignUpNavigationMenu state={navigationItems} activeItem={activeItem} redirectTo={redirectTo} />
          <Content className="content">
            <Skeleton active loading={prospectSignupState === undefined}>
              {navigationItems.length > 0 && prospectSignupState !== undefined
                ? DynamicContentBasedOnRoutes(goBack, goNext, setHasDirtyData)
                : null}
            </Skeleton>
          </Content>
        </AntLayout>
      </AntLayout>
    </Skeleton>
  )
}

export default ProspectSinup
